import React, { useEffect } from "react";
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { createTheme, ThemeProvider } from '@mui/material/styles';
import usePageTracking from './hooks/usePageTracking';
import { EnumProvider } from './hooks/useEnums';
import { DataProvider } from './hooks/useDataFetching';
import ScrollToTop from './components/ScrollToTop';
import tokenManager from './services/tokenManager';

import "./App.css"; // Import CSS file
import Footer from "./components/Footer";
import Header from "./components/Header";
import FeedbackBanner from "./components/FeedbackBanner";
import BuyMeACroissantBanner from "./components/BuyMeACroissantBanner";
import Home from "./pages/Home";
import About from "./pages/About";
import Database from "./pages/Database";
import Comparison from "./pages/Comparison";
import Recommendations from "./pages/Recommendations";
import Product from './pages/Product';
import Tools from './pages/Tools';
import Faq from './pages/Faq';
import Contact from './pages/Contact';
import Scanner from './pages/Scanner';
import Quiz from './pages/Quiz';
import Chatbot from './pages/Chatbot';
import AmazonDisclosure from './pages/AmazonDisclosure';
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsOfUse from './pages/TermsOfUse';
import Blog from './pages/Blog';
import BlogArticle from './pages/BlogArticle';

const theme = createTheme({
  typography: {
    fontFamily: 'Poppins, sans-serif',
    fontWeightRegular: 400,
    fontWeightMedium: 500,
    fontWeightBold: 600,

    h1: { fontWeight: 500 },
    h2: { fontWeight: 500 },
    h3: { fontWeight: 500 },
    h4: { fontWeight: 500 },
    h5: { fontWeight: 400 },
    h6: { fontWeight: 400 },
    body1: { fontWeight: 400 },
    body2: { fontWeight: 400 },
    subtitle1: { fontWeight: 400 },
    subtitle2: { 
      fontWeight: 600,
    },
  },
});

const AppContent = () => {
  usePageTracking(); // Add page tracking

  // Initialize security token when app starts
  useEffect(() => {
    tokenManager.initialize();
    
    // Cleanup when component unmounts
    return () => {
      tokenManager.cleanup();
    };
  }, []);

  // Get current location to determine if feedback banner should be shown
  const location = useLocation();
  
  // Define which pages should show the feedback banner
  const shouldShowFeedbackBanner = () => {
    const feedbackPages = [
      '/Faq',
      '/Contact'
    ];
    
    return feedbackPages.includes(location.pathname);
  };

  // Define which pages should show the buy me a croissant banner
  const shouldShowBuyMeACroissantBanner = () => {
    const buyMeACroissantPages = [
      '/Database',
      '/Scanner',
      '/Comparison',
      '/Quiz',
      '/Tools'
    ];
    
    return buyMeACroissantPages.includes(location.pathname);
  };

  return (
    <>
      <Header />
      <div className="app-scroll-container">
        <div className="flex flex-col min-h-screen">
          <main className="flex-grow">
            <Routes>
              <Route path="/" element={<Home />} />
              <Route path="/About" element={<About />} />
              <Route path="/Search" element={<Navigate to="/Database" replace />} />
              <Route path="/Database" element={<Database />} />
              <Route path="/Comparison" element={<Comparison />} />
              <Route path="/Tools" element={<Tools />} />
              <Route path="/Recommendations" element={<Recommendations />} />
              <Route path="/Quiz" element={<Quiz />} />
              <Route path="/Chatbot" element={<Chatbot />} />
              <Route path="/Faq" element={<Faq />} />
              <Route path="/Contact" element={<Contact />} />
              <Route path="/Blog" element={<Blog />} />
              <Route path="/Blog/:slug" element={<BlogArticle />} />
              <Route path="/Product/:id" element={<Product />} />
              <Route path="/Scan" element={<Navigate to="/Scanner" replace />} />
              <Route path="/Scanner" element={<Scanner />} />
              <Route path="/amazon-disclosure" element={<AmazonDisclosure />} />
              <Route path="/privacy-policy" element={<PrivacyPolicy />} />
              <Route path="/terms-of-use" element={<TermsOfUse />} />
            </Routes>
          </main>
          {shouldShowBuyMeACroissantBanner() && <BuyMeACroissantBanner />}
          {shouldShowFeedbackBanner() && <FeedbackBanner />}
          <Footer />
        </div>
      </div>
    </>
  );
};

const App = () => {
  return (
    <ThemeProvider theme={theme}>
      <EnumProvider>
        <DataProvider>
          <Router>
            <ScrollToTop />
            <AppContent />
          </Router>
        </DataProvider>
      </EnumProvider>
    </ThemeProvider>
  );
};

export default App;
